import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { getTransactionById, getInvoiceById } from "../../services/api";
import { formatAmount, getMaxDecimalsForCurrency } from "../../utils";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n, ccy = "USD") =>
  n != null ? `${ccy} ${formatAmount(n,getMaxDecimalsForCurrency(ccy))}` : "—";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

const statusLabel = (s) => {
  if (s === "completed" || s === "withdrawn" || s === "success") return "Settled";
  if (s === "pending" || s === "processing" || s === "created") return "Pending";
  if (s === "failed" || s === "rejected") return "Failed";
  if (s === "canceled") return "Canceled";
  return s ?? "—";
};

const invoiceStatusLabel = (s) => {
  if (s === "success" || s === "fulfilled") return "Paid";
  if (s === "processing" || s === "quote_locked") return "In Progress";
  if (s === "created") return "Pending";
  return s ?? "—";
};

const statusBadgeClass = (s) => {
  const l = statusLabel(s);
  if (l === "Settled") return "bg-green-50 text-green-700 border border-green-200";
  if (l === "Pending") return "bg-orange-50 text-orange-700 border border-orange-200";
  if (l === "Failed" || l === "Canceled")
    return "bg-red-50 text-red-700 border border-red-200";
  return "bg-gray-50 text-gray-600 border border-gray-200";
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between py-3 border-b border-gray-100 last:border-0">
    <span className="text-sm text-gray-500 w-44 flex-shrink-0">{label}</span>
    <span className="text-sm font-medium text-gray-900 text-right break-all">{value ?? "—"}</span>
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5">
    <h3 className="text-sm font-semibold text-gray-700 mb-3 pb-2 border-b border-gray-100">
      {title}
    </h3>
    {children}
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────
const SenderTransactionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [txn, setTxn] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getTransactionById(id)
      .then(async (res) => {
        const t = res.data || null;
        setTxn(t);
        // If there's a linked invoice, fetch it too (non-blocking)
        const invId =
          t?.paymentRequestId ||
          t?.invoiceId ||
          t?.paymentRequest?.id ||
          t?.invoice?.id;
        if (invId) {
          try {
            const invRes = await getInvoiceById(invId);
            setInvoice(invRes.data || null);
          } catch {
            // invoice fetch failure is non-blocking
          }
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  // ─── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <AppShell>
        <div className="animate-pulse space-y-5">
          <div className="h-6 bg-gray-200 rounded w-48" />
          <div className="h-8 bg-gray-200 rounded w-72" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="h-64 bg-gray-200 rounded-xl" />
            <div className="h-64 bg-gray-200 rounded-xl" />
            <div className="h-48 bg-gray-200 rounded-xl" />
            <div className="h-48 bg-gray-200 rounded-xl" />
          </div>
        </div>
      </AppShell>
    );
  }

  // ─── Error / not found ─────────────────────────────────────────────────────
  if (error || !txn) {
    return (
      <AppShell>
        <button
          onClick={() => navigate("/sender/transactions")}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back to Transactions
        </button>
        <p className="text-gray-500">Transaction not found.</p>
      </AppShell>
    );
  }

  // ─── Derived values ────────────────────────────────────────────────────────
  // API returns transactionId (not id); bank details under bankDetails (not bank)
  const txnId      = txn.transactionId || txn.id;
  const bankInfo   = txn.bankDetails   || txn.bank;

  const fromCurrency = txn.quote?.fromCurrency ?? "USDC";
  const toCurrency   = txn.quote?.toCurrency   ?? "USD";
  const fromAmount   = txn.quote?.fromAmount;
  const toAmount     = txn.quote?.toAmount;
  // displayRate = fromAmount per 1 toCurrency unit (e.g. 1379 NGN per 1 USD)
  const displayRate  =
    fromAmount && toAmount && Number(toAmount) > 0
      ? Number(fromAmount) / Number(toAmount)
      : txn.quote?.rate != null
      ? Number(txn.quote.rate)
      : null;

  const receiverName = txn.toDetails?.name || txn.receiver?.name || "—";

  return (
    <AppShell>
      {/* Back */}
      <button
        onClick={() => navigate("/sender/transactions")}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
        </svg>
        Back to Transactions
      </button>

      {/* Page header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Transaction Detail</h1>
        </div>
        <span
          className={`text-sm font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ml-4 ${statusBadgeClass(txn.status)}`}
        >
          {statusLabel(txn.status)}
        </span>
      </div>
      {/* Amount highlight bar */}
      {txn.status === "success" && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl px-5 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-green-500 uppercase tracking-wider">
              Amount Sent
            </p>
            <p className="text-3xl font-bold text-green-700 mt-0.5">
              {fromAmount != null ? `${fromCurrency} ${Number(fromAmount).toLocaleString()}` : "—"}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* ── Transaction summary ──────────────────────────────────────────── */}
        <SectionCard title="Transaction Summary">
          <DetailRow label="Transaction ID" value={txnId} />
          {txn.partnerTxnId && (
            <DetailRow label="Partner Txn ID" value={txn.partnerTxnId} />
          )}
          <DetailRow label="Status" value={statusLabel(txn.status)} />
          <DetailRow label="Created" value={formatDate(txn.createdAt)} />
          <DetailRow label="Updated" value={formatDate(txn.updatedAt)} />
          <DetailRow label="Receiver" value={receiverName} />
        </SectionCard>

                {/* ── Exchange rate / quote ────────────────────────────────────────── */}
        <SectionCard title="Exchange Rate">
          {txn.quote ? (
            <>
              <DetailRow
                label="You Sent"
                value={`${fromCurrency} ${fromAmount != null ? Number(fromAmount).toLocaleString() : "—"}`}
              />
              <DetailRow
                label="Receiver Gets"
                value={`${toCurrency} ${toAmount != null ? Number(toAmount).toLocaleString() : "—"}`}
              />
              <DetailRow
                label="Rate"
                value={
                  displayRate != null
                    ? `1 ${toCurrency} = ${displayRate.toFixed(4)} ${fromCurrency}`
                    : "—"
                }
              />
              {txn.quote.fee != null && (
                <DetailRow label="Fee" value={fmt(txn.quote.fee)} />
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400">No rate information available.</p>
          )}
        </SectionCard>

        {/* ── Invoice info ─────────────────────────────────────────────────── */}
        {(() => {
          const inv = invoice || txn.paymentRequest;
          return inv ? (
            <SectionCard title="Invoice Details">
              <DetailRow label="Reference" value={inv.invoiceId || inv.referenceNumber} />
              <DetailRow
                label="Invoice Amount"
                value={toAmount != null ? `${toCurrency} ${formatAmount(toAmount,getMaxDecimalsForCurrency(toCurrency))}` : "—"}
              />
              <DetailRow label="Currency" value={toCurrency} />
              {inv.description && (
                <DetailRow label="Description" value={inv.description} />
              )}
              {inv.dueDate && (
                <DetailRow
                  label="Due Date"
                  value={new Date(inv.dueDate).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                />
              )}
              <DetailRow label="Invoice Status" value={invoiceStatusLabel(inv.status)} />
            </SectionCard>
          ) : (
            <SectionCard title="Invoice Details">
              <p className="text-sm text-gray-400">No invoice details available for this transaction.</p>
            </SectionCard>
          );
        })()}

        {/* ── Receiver / bank details ──────────────────────────────────────── */}
        <SectionCard title="Receiver & Bank">
          {txn.toDetails || txn.receiver ? (
            <>
              <DetailRow label="Name" value={txn.toDetails?.name || txn.receiver?.name} />
              {(txn.toDetails?.email || txn.receiver?.email) && (
                <DetailRow label="Email" value={txn.toDetails?.email || txn.receiver?.email} />
              )}
              {(txn.toDetails?.phone || txn.receiver?.phone) && (
                <DetailRow label="Phone" value={txn.toDetails?.phone || txn.receiver?.phone} />
              )}
              {(txn.toDetails?.country || txn.receiver?.country) && (
                <DetailRow label="Country" value={txn.toDetails?.country || txn.receiver?.country} />
              )}
              {bankInfo && (
                <>
                  <DetailRow label="Bank" value={bankInfo.bankName} />
                  <DetailRow label="Account Holder" value={bankInfo.accountHolderName} />
                  <DetailRow label="Account Number" value={bankInfo.accountNumber} />
                  {bankInfo.routingNumber && (
                    <DetailRow label="Routing Number" value={bankInfo.routingNumber} />
                  )}
                  {bankInfo.swiftCode && (
                    <DetailRow label="SWIFT / BIC" value={bankInfo.swiftCode} />
                  )}
                </>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400">No receiver information available.</p>
          )}
        </SectionCard>
      </div>
    </AppShell>
  );
};

export default SenderTransactionDetail;
